// Pure helpers for the identity review page (self sign-ups whose selfie / ID
// photo the AI could not confirm). No React here so they can be unit-tested.

export type IdentityStatus = 'unverified' | 'verified' | 'needs_review' | 'declined';
export type IdentityPhotoKind = 'selfie' | 'id';

export type IdentityAiVerdict = {
  verdict: 'approve' | 'reject' | 'unsure';
  confidence: number;
  reason: string;
  model: string;
};

export type IdentityReviewRow = {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  identityStatus: IdentityStatus;
  photoUrl: string | null;
  photoKind: IdentityPhotoKind | null;
  aiVerdict: IdentityAiVerdict | null;
  attempts: number;
  checkedAt: Date | string | null;
  createdAt: Date | string | null;
};

export const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const studentLabel = (row: Pick<IdentityReviewRow, 'firstName' | 'lastName' | 'email'>): string =>
  `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || row.email || '';

/** i18n key suffix for the status chip (`identityReviews.status.<key>`). */
export const identityStatusKey = (status: IdentityStatus | null | undefined): string => {
  switch (status) {
    case 'verified':
      return 'verified';
    case 'needs_review':
      return 'needsReview';
    case 'declined':
      return 'declined';
    default:
      return 'unverified';
  }
};

/** Chip tone per status: amber while a human must look, red when declined, green when fine. */
export const identityStatusTone = (
  status: IdentityStatus | null | undefined,
): 'pending' | 'declined' | 'verified' | 'none' => {
  switch (status) {
    case 'needs_review':
      return 'pending';
    case 'declined':
      return 'declined';
    case 'verified':
      return 'verified';
    default:
      return 'none';
  }
};

/** AI confidence as a whole percentage (0–100); null when there is no verdict. */
export const verdictPercent = (verdict: IdentityAiVerdict | null | undefined): number | null => {
  if (!verdict || typeof verdict.confidence !== 'number' || Number.isNaN(verdict.confidence)) return null;
  const pct = verdict.confidence <= 1 ? verdict.confidence * 100 : verdict.confidence;
  return Math.max(0, Math.min(100, Math.round(pct)));
};

/** Rows a human must act on first (needs_review), then declined, then the rest; oldest first inside each. */
export const sortReviews = <T extends Pick<IdentityReviewRow, 'identityStatus' | 'createdAt'>>(rows: T[]): T[] => {
  const rank = (status: IdentityStatus) =>
    status === 'needs_review' ? 0 : status === 'declined' ? 1 : status === 'unverified' ? 2 : 3;
  return [...rows].sort((a, b) => {
    const r = rank(a.identityStatus) - rank(b.identityStatus);
    if (r !== 0) return r;
    return (toDate(a.createdAt)?.getTime() ?? 0) - (toDate(b.createdAt)?.getTime() ?? 0);
  });
};

/** Only rows still waiting for a decision count toward the "pending" number. */
export const countPending = (rows: Pick<IdentityReviewRow, 'identityStatus'>[]): number =>
  rows.filter((row) => row.identityStatus === 'needs_review').length;

/** Review page filter: one status, or everything the backend sent. */
export type ReviewFilter = 'all' | 'needs_review' | 'declined' | 'unverified';
export const REVIEW_FILTERS: ReviewFilter[] = ['all', 'needs_review', 'declined', 'unverified'];

export const filterReviews = <T extends Pick<IdentityReviewRow, 'identityStatus'>>(rows: T[], filter: ReviewFilter): T[] =>
  filter === 'all' ? rows : rows.filter((row) => row.identityStatus === filter);
